// services/billingApi.ts — HTTP client for /api/billing/*.
// Endpoints, request shapes and response shapes match BILLING_CONTRACT.md §3.
// All endpoints are authed and go through authedFetch (handles bearer + 401 refresh).

import { authedFetch } from "../auth/tokens";

// ── Types ───────────────────────────────────────────────────────────────

export type SubscriptionStatus =
  | "active"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "trialing"
  | "unpaid";

export interface BillingSubscription {
  id: string;
  status: SubscriptionStatus | string;
  currentPeriodEnd: string | null;
}

export interface BillingTransaction {
  id: string;
  kind: string;
  delta: number;
  balanceAfter: number;
  createdAt: string;
}

export interface BillingStatus {
  balance: number;
  subscription: BillingSubscription | null;
  recentTransactions: BillingTransaction[];
}

export type CheckoutKind = "subscription" | "topup";

// ── Helper: parse JSON; throw an Error from {error,code} bodies ─────────
// Mirrors authApi.ts's behaviour so callers see consistent error shapes.

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON body — fall through.
    }
  }
  if (!res.ok) {
    const errBody = (body as { error?: string; code?: string }) || {};
    const msg =
      errBody.error ||
      (text ? text.slice(0, 200) : `Request failed with status ${res.status}`);
    const err = new Error(msg) as Error & {
      code?: string;
      status?: number;
      body?: unknown;
    };
    if (errBody.code) err.code = errBody.code;
    err.status = res.status;
    // Stash the full parsed body (or null if non-JSON) so callers that need
    // additional structured fields (e.g. attemptsRemaining on /extension/redeem)
    // can read them without a second parse.
    err.body = body;
    throw err;
  }
  return body as T;
}

// ── Endpoints ───────────────────────────────────────────────────────────

/**
 * GET /api/billing/status — current balance, subscription, recent transactions.
 */
export async function getBillingStatus(): Promise<BillingStatus> {
  const res = await authedFetch(`/api/billing/status`, { method: "GET" });
  return parseJsonOrThrow<BillingStatus>(res);
}

/**
 * POST /api/billing/checkout — returns a Stripe Checkout Session URL the
 * caller should redirect to via window.location.href.
 */
export async function startCheckout(
  kind: CheckoutKind
): Promise<{ url: string }> {
  const res = await authedFetch(`/api/billing/checkout`, {
    method: "POST",
    body: JSON.stringify({ kind }),
  });
  return parseJsonOrThrow<{ url: string }>(res);
}

/**
 * POST /api/billing/portal — returns a Stripe billing portal URL for
 * managing the subscription (cancel, update payment method).
 */
export async function openBillingPortal(): Promise<{ url: string }> {
  const res = await authedFetch(`/api/billing/portal`, { method: "POST" });
  return parseJsonOrThrow<{ url: string }>(res);
}

/**
 * POST /api/billing/extension/request — admin-gated testing surface. Sends a
 * 6-digit approval code to the configured admin email. Returns 404 when the
 * EXTENSION_REQUESTS_ENABLED env flag is off (which is its production state).
 *
 * See BILLING_CONTRACT.md §10.
 */
export async function requestExtension(): Promise<{ ok: true }> {
  const res = await authedFetch(`/api/billing/extension/request`, {
    method: "POST",
  });
  return parseJsonOrThrow<{ ok: true }>(res);
}

/**
 * POST /api/billing/extension/redeem — exchange the 6-digit code for +50
 * turns. On success returns the new balance. On invalid code returns 400 with
 * `code = "invalid_code"` (and an `attemptsRemaining` field via the Error's
 * status; callers should display the server-supplied error message).
 */
export async function redeemExtension(
  code: string,
): Promise<{ ok: true; balance: number }> {
  const res = await authedFetch(`/api/billing/extension/redeem`, {
    method: "POST",
    body: JSON.stringify({ code }),
  });
  return parseJsonOrThrow<{ ok: true; balance: number }>(res);
}
